import { BackgroundMenu } from './BackgroundMenu/BackgroundMenu'
import { Box, Stack } from '@dtx-company/design-system/src'
import { CarouselContext, CarouselProvider, Slide, Slider } from 'pure-react-carousel'
import {
  ColorLensOutlined,
  DesignServicesOutlined,
  FontDownloadOutlined,
  Link,
  PhotoSizeSelectActualOutlined
} from '@mui/icons-material'
import { ColorMenu } from './ColorMenu/ColorMenu'
import { DesignTabs } from './DesignMenu.types'
import { FC, ReactNode, useContext, useEffect, useState } from 'react'
import { FontMenu } from '../FontMenu/FontMenu'
import { LinksMenu } from './LinksMenu/LinksMenu'
import { MenuItem } from '../NewMenuContent/MenuItem'
import { RemoveBrandingMenu } from './RemoveBrandingMenu/RemoveBrandingMenu'
import { ResetPageTheme } from '../../../Customize/ResetPageTheme'
import { useCurrentPage } from '@dtx-company/inter-app/src/redux/slices/utils/currentPage'
import { useDesignMenuState } from './DesignMenu.hooks'
import { useShowResetCustomizationOptions } from '../../../../FlowpageTemplates/hooks/useShowResetCustomizationOptions'

const TABS_LIST = [
  DesignTabs.COLOR,
  DesignTabs.LINKS,
  DesignTabs.BACKGROUND,
  DesignTabs.TEXT,
  DesignTabs.BRANDING
]

const DesignMenuTabs: FC = () => {
  const carouselContext = useContext(CarouselContext)
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide)

  // Subscribe to changes to the current slide index
  useEffect(() => {
    const onChange = (): void => {
      setCurrentSlide(carouselContext.state.currentSlide)
    }
    carouselContext.subscribe(onChange)
    return () => carouselContext.unsubscribe(onChange)
  }, [carouselContext])

  return (
    <Stack
      sx={{
        width: '100%',
        justifyContent: 'space-around',
        alignItems: 'flex-end',
        flexDirection: 'row'
      }}
    >
      <MenuItem
        Icon={<ColorLensOutlined fontSize="medium" />}
        onClick={() => carouselContext.setStoreState({ currentSlide: 0 })}
        selected={TABS_LIST[currentSlide] === DesignTabs.COLOR}
        label="Color"
      />
      <MenuItem
        Icon={<Link fontSize="medium" />}
        onClick={() => carouselContext.setStoreState({ currentSlide: 1 })}
        label="Links"
        selected={TABS_LIST[currentSlide] === DesignTabs.LINKS}
      />
      <MenuItem
        Icon={<PhotoSizeSelectActualOutlined fontSize="medium" />}
        onClick={() => carouselContext.setStoreState({ currentSlide: 2 })}
        label="Background"
        selected={TABS_LIST[currentSlide] === DesignTabs.BACKGROUND}
      />
      <MenuItem
        Icon={<FontDownloadOutlined fontSize="medium" />}
        onClick={() => carouselContext.setStoreState({ currentSlide: 3 })}
        label="Text"
        selected={TABS_LIST[currentSlide] === DesignTabs.TEXT}
      />
      <MenuItem
        Icon={<DesignServicesOutlined fontSize="medium" />}
        onClick={() => carouselContext.setStoreState({ currentSlide: 4 })}
        label="Settings"
        selected={TABS_LIST[currentSlide] === DesignTabs.BRANDING}
      />
    </Stack>
  )
}

const MenuWrapper: FC<{ height?: string; children: ReactNode }> = ({ height, children }) => (
  <Box
    sx={theme => ({
      maxHeight: height
        ? `calc(${height} - ${theme.spacing(27.5)})`
        : `calc(100% - ${theme.spacing(27.5)})`,
      p: theme.spacing(3, 0.5, 4),
      overflowY: 'scroll'
    })}
  >
    {children}
  </Box>
)

export const DesignMenu: FC<{ height?: string }> = ({ height }) => {
  const { pageTheme } = useDesignMenuState()
  const page = useCurrentPage()
  const showResetCustomizeOptions = useShowResetCustomizationOptions({ page })

  return (
    <Box
      sx={{
        width: '100%',
        '& > .carousel': {
          width: '100%'
        }
      }}
    >
      <CarouselProvider
        naturalSlideHeight={400}
        naturalSlideWidth={300}
        totalSlides={5}
        isIntrinsicHeight
      >
        <Stack
          sx={theme => ({
            p: theme.spacing(6, 4, 0, 4),
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          })}
        >
          <DesignMenuTabs />
          <Box
            sx={{
              width: '100%',
              position: 'relative'
            }}
          >
            <Slider>
              <Slide index={0}>
                <MenuWrapper height={height}>
                  <ColorMenu pageTheme={pageTheme} />
                </MenuWrapper>
              </Slide>
              <Slide index={1}>
                <MenuWrapper height={height}>
                  <LinksMenu pageTheme={pageTheme} />
                </MenuWrapper>
              </Slide>
              <Slide index={2}>
                <MenuWrapper height={height}>
                  <BackgroundMenu pageTheme={pageTheme} />
                </MenuWrapper>
              </Slide>
              <Slide index={3}>
                <MenuWrapper height={height}>
                  <FontMenu pageTheme={pageTheme} />
                </MenuWrapper>
              </Slide>
              <Slide index={4}>
                <MenuWrapper height={height}>
                  <RemoveBrandingMenu />
                  {showResetCustomizeOptions && <ResetPageTheme themeId={pageTheme?.id || ''} />}
                </MenuWrapper>
              </Slide>
            </Slider>
          </Box>
        </Stack>
      </CarouselProvider>
    </Box>
  )
}
